'use client';

import { useState } from 'react';
import Image from 'next/image';

const listings = [
  {
    id: 1,
    name: 'EmpowerHer Gym',
    type: 'Women-Only Gym',
    tags: ['Yoga', 'Personal Training', 'Pool'],
    description: 'A premium women-only fitness center with state-of-the-art equipment, yoga studio, and indoor swimming pool.',
    image: '/gym-1.jpg',
  },
  {
    id: 2,
    name: 'Sarah Johnson Fitness',
    type: 'Personal Trainer',
    tags: ['Strength', 'HIIT', 'Nutrition'],
    description: 'Certified female personal trainer specializing in strength training and body transformation programs.',
    image: '/trainer-1.jpg',
  },
];

const DirectorySearch = () => {
  const [viewMode, setViewMode] = useState<'list' | 'map'>('map');

  return (
    <section className="py-20 bg-[#f5f2f0]">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[24px] p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-[#8a7490] tracking-wide uppercase mb-3">
              Find Local Female Only Gyms
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2b262e] mb-4">
              Searching for your local female only gym, fitness centre, or personal trainer has never been easier
            </h2>
            <p className="text-[#6e6672] max-w-2xl mx-auto">
              Browse our directory of verified women-only fitness spaces and female personal trainers. Filter by location, activities, amenities and more.
            </p>
          </div>

          {/* Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-[#f5f2f0] rounded-xl p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[#8a7490] text-white'
                    : 'text-[#6e6672] hover:text-[#2b262e]'
                }`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'map'
                    ? 'bg-[#8a7490] text-white'
                    : 'text-[#6e6672] hover:text-[#2b262e]'
                }`}
              >
                Map
              </button>
            </div>
          </div>

          {/* Map Preview */}
          <div className="relative h-[400px] rounded-[20px] overflow-hidden mb-8 bg-[#e8e1e6]">
            {/* Map placeholder - replace with actual map component */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-[#6e6672] mb-4">Interactive Map Preview</p>
                <div className="flex items-center justify-center gap-4">
                  <div className="w-4 h-4 bg-[#8a7490] rounded-full" />
                  <span className="text-sm text-[#6e6672]">Locations near you</span>
                </div>
              </div>
            </div>
            {/* Simulated map markers */}
            <div className="absolute top-1/4 left-1/4 w-8 h-8 bg-[#8a7490] rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              </svg>
            </div>
            <div className="absolute top-1/3 right-1/3 w-8 h-8 bg-[#8a7490] rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              </svg>
            </div>
            <div className="absolute bottom-1/3 left-1/2 w-8 h-8 bg-[#8a7490] rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              </svg>
            </div>
          </div>

          {/* Listing Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {listings.map((listing) => (
              <div key={listing.id} className="flex gap-4 p-4 rounded-[16px] bg-[#f5f2f0]">
                <div className="relative w-32 h-32 rounded-[12px] overflow-hidden flex-shrink-0">
                  <Image
                    src={listing.image}
                    alt={listing.name}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-[#2b262e] mb-1">{listing.name}</h4>
                  <p className="text-sm text-[#8a7490] mb-2">{listing.type}</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {listing.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs bg-[#8a7490]/10 text-[#8a7490] rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-[#6e6672] line-clamp-2">{listing.description}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button className="p-2 text-[#8a7490] hover:bg-[#8a7490]/10 rounded-lg transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button className="p-2 text-[#8a7490] hover:bg-[#8a7490]/10 rounded-lg transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Show More Button */}
          <div className="text-center">
            <button className="px-8 py-3 bg-[#8a7490] text-white font-medium rounded-xl hover:bg-[#7a647d] transition-colors">
              Show More
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DirectorySearch;
